import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import moment from "moment";

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function fetchUser() {
      setLoading(true);
      const ref = doc(db, "users", id);
      const docSnap = await getDoc(ref);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUser(data);
        setEditFields({
          firstName: data.firstname || "",
          lastName: data.lastname || "",
          ribBic: data.ribBic || "",
          iban: data.iban || "",
        });
      }
      setLoading(false);
    }
    fetchUser();
  }, [id]);

  const showNotif = (msg) => {
    const div = document.createElement("div");
    div.className =
      "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 text-sm";
    div.textContent = msg;
    document.body.appendChild(div);
    setTimeout(() => div.remove(), 3000);
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, "users", id), {
        firstname: editFields.firstName,
        lastname: editFields.lastName,
        ribBic: editFields.ribBic,
        iban: editFields.iban,
      });
      setUser((prev) => ({
        ...prev,
        firstname: editFields.firstName,
        lastname: editFields.lastName,
        ribBic: editFields.ribBic,
        iban: editFields.iban,
      }));
      showNotif("Modifications sauvegardées !");
    } catch (e) {
      alert("Erreur lors de la sauvegarde");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    try {
      await updateDoc(doc(db, "users", id), { isActive: !user.isActive });
      setUser((prev) => ({ ...prev, isActive: !prev.isActive }));
      showNotif(`Compte ${!user.isActive ? "activé" : "désactivé"} !`);
    } catch {
      alert("Erreur lors de la mise à jour");
    }
  };

  const toggleVerified = async () => {
    try {
      await updateDoc(doc(db, "users", id), { emailIsVerified: !user.emailIsVerified });
      setUser((prev) => ({ ...prev, emailIsVerified: !prev.emailIsVerified }));
      showNotif(`Compte ${!user.emailIsVerified ? "vérifié" : "dévérifié"} !`);
    } catch {
      alert("Erreur lors de la mise à jour");
    }
  };

  if (!user)
    return (
      <PageTemplate
        loading={loading}
        content={
          <div className="text-center py-12">
            <p className="text-sm text-gray-500">Utilisateur introuvable</p>
            <button
              onClick={() => navigate("/users")}
              className="mt-2 text-xs font-medium text-[#801111] hover:text-[#5a0c0c]"
            >
              Retour à la liste
            </button>
          </div>
        }
      />
    );

  const hasDocuments = user.pieceRecto && user.pieceVerso;

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-4">
          {/* Back */}
          <button
            onClick={() => navigate("/users")}
            className="text-xs font-medium text-[#801111] hover:text-[#5a0c0c]"
          >
            ← Retour à la liste
          </button>

          {/* Header: photo + name + statuts */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-4">
              {user.photo ? (
                <img
                  src={user.photo}
                  alt=""
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold">
                  {user.firstname?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="text-sm font-semibold text-gray-900">
                  {user.firstname} {user.lastname}
                </h1>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
              <div className="flex items-center gap-3">
                {/* Statut Actif */}
                <div className="text-center">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Actif</p>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                    user.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}>
                    {user.isActive ? "Oui" : "Non"}
                  </span>
                </div>
                {/* Statut Vérifié */}
                <div className="text-center">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Vérifié</p>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                    user.emailIsVerified ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                  }`}>
                    {user.emailIsVerified ? "Oui" : "Non"}
                  </span>
                </div>
                {/* Type */}
                <div className="text-center">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Type</p>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                    user.isClient ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
                  }`}>
                    {user.isClient ? "Client" : "Couturier"}
                  </span>
                </div>
                {/* Créé le */}
                <div className="text-center">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Créé le</p>
                  <span className="text-xs text-gray-700">
                    {user.createdAt ? moment(user.createdAt.toDate()).format("DD/MM/YY") : "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Col 1+2: Formulaire */}
            <div className="lg:col-span-2 space-y-4">
              {/* Infos modifiables */}
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="px-3 py-2 border-b border-gray-200">
                  <span className="text-xs font-medium text-gray-900">Informations modifiables</span>
                </div>
                <div className="p-3 space-y-3">
                  {/* Row: Prénom + Nom */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Prénom</label>
                      <input
                        type="text"
                        value={editFields.firstName}
                        onChange={(e) => setEditFields((f) => ({ ...f, firstName: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Nom</label>
                      <input
                        type="text"
                        value={editFields.lastName}
                        onChange={(e) => setEditFields((f) => ({ ...f, lastName: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
                      />
                    </div>
                  </div>

                  {/* Row: IBAN + BIC */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">IBAN</label>
                      <input
                        type="text"
                        value={editFields.iban}
                        onChange={(e) => setEditFields((f) => ({ ...f, iban: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-gray-400"
                        placeholder="FR76 XXXX XXXX XXXX"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">BIC/SWIFT</label>
                      <input
                        type="text"
                        value={editFields.ribBic}
                        onChange={(e) => setEditFields((f) => ({ ...f, ribBic: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-gray-400"
                        placeholder="XXXXXXXX"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      disabled={saving}
                      onClick={saveChanges}
                      className="px-4 py-1.5 bg-[#801111] text-white rounded-md text-xs font-medium hover:bg-[#5a0c0c] transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? "Sauvegarde..." : "Sauvegarder"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Statuts & Actions */}
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="px-3 py-2 border-b border-gray-200">
                  <span className="text-xs font-medium text-gray-900">Statuts du compte</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {/* Compte actif — seul statut modifiable librement */}
                  <div className="flex items-center justify-between px-3 py-3">
                    <div>
                      <p className="text-xs font-medium text-gray-900">Compte actif</p>
                      <p className="text-[10px] text-gray-500">
                        {user.isActive
                          ? "L'utilisateur peut se connecter et utiliser l'app"
                          : "L'utilisateur est bloqué et ne peut pas accéder à l'app"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${user.isActive ? "text-green-600" : "text-red-600"}`}>
                        {user.isActive ? "Activé" : "Désactivé"}
                      </span>
                      <button
                        onClick={toggleActive}
                        className={`px-3 py-1 rounded text-xs font-medium text-white transition ${
                          user.isActive
                            ? "bg-red-600 hover:bg-red-700"
                            : "bg-green-600 hover:bg-green-700"
                        }`}
                      >
                        {user.isActive ? "Désactiver" : "Activer"}
                      </button>
                    </div>
                  </div>

                  {/* Type — lecture seule */}
                  <div className="flex items-center justify-between px-3 py-3">
                    <div>
                      <p className="text-xs font-medium text-gray-900">Type de compte</p>
                      <p className="text-[10px] text-gray-500">
                        {user.isClient
                          ? "Compte client — recherche des couturiers"
                          : "Compte couturier — propose des services"}
                      </p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      user.isClient ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
                    }`}>
                      {user.isClient ? "Client" : "Couturier"}
                    </span>
                  </div>

                  {/* Compte vérifié — modifiable seulement si documents envoyés */}
                  <div className="flex items-center justify-between px-3 py-3">
                    <div>
                      <p className="text-xs font-medium text-gray-900">Vérification identité</p>
                      <p className="text-[10px] text-gray-500">
                        {!hasDocuments
                          ? "Aucun document envoyé — la vérification n'est pas possible"
                          : user.emailIsVerified
                          ? "Documents conformes, identité validée par un admin"
                          : "Documents reçus, en attente de validation admin"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${user.emailIsVerified ? "text-green-600" : hasDocuments ? "text-orange-500" : "text-gray-400"}`}>
                        {user.emailIsVerified ? "Vérifié" : hasDocuments ? "En attente" : "Non vérifié"}
                      </span>
                      {hasDocuments && (
                        <button
                          onClick={toggleVerified}
                          className={`px-3 py-1 rounded text-xs font-medium text-white transition ${
                            user.emailIsVerified
                              ? "bg-orange-500 hover:bg-orange-600"
                              : "bg-green-600 hover:bg-green-700"
                          }`}
                        >
                          {user.emailIsVerified ? "Dévérifier" : "Vérifier"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Col 3: Documents */}
            <div className="space-y-4">
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="px-3 py-2 border-b border-gray-200">
                  <span className="text-xs font-medium text-gray-900">Documents d'identité</span>
                </div>
                <div className="p-3">
                  {hasDocuments ? (
                    <div className="space-y-3">
                      <a
                        href={user.pieceRecto}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block border border-gray-200 rounded-md overflow-hidden hover:border-[#801111] transition-colors"
                      >
                        <img
                          src={user.pieceRecto}
                          alt="Recto"
                          loading="lazy"
                          className="w-full h-28 object-contain bg-gray-50"
                        />
                        <p className="text-center text-[10px] text-gray-500 py-1 bg-gray-50 border-t border-gray-100">Recto</p>
                      </a>
                      <a
                        href={user.pieceVerso}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block border border-gray-200 rounded-md overflow-hidden hover:border-[#801111] transition-colors"
                      >
                        <img
                          src={user.pieceVerso}
                          alt="Verso"
                          loading="lazy"
                          className="w-full h-28 object-contain bg-gray-50"
                        />
                        <p className="text-center text-[10px] text-gray-500 py-1 bg-gray-50 border-t border-gray-100">Verso</p>
                      </a>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-xs text-gray-500">Aucun document reçu</p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        L'utilisateur n'a pas encore fourni ses documents
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Infos supplémentaires */}
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="px-3 py-2 border-b border-gray-200">
                  <span className="text-xs font-medium text-gray-900">Informations</span>
                </div>
                <div className="p-3 space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email</span>
                    <span className="text-gray-900 font-medium truncate ml-2">{user.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Téléphone</span>
                    <span className="text-gray-900">{user.phoneNumber || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Ville</span>
                    <span className="text-gray-900">{user.city || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Département</span>
                    <span className="text-gray-900">{user.department || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Type</span>
                    <span className="text-gray-900">{user.type || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Créé le</span>
                    <span className="text-gray-900">
                      {user.createdAt ? moment(user.createdAt.toDate()).format("DD/MM/YYYY HH:mm") : "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}
